import {
  IUnifiedFilter,
  IFilteredCountsSummary,
  MemoryModel,
  MediaModel,
  ContactModel,
  Contact,
  GroupModel,
  ShareModel,
  ActivityLogModel,
  MessageModel,
  PlaceModel,
  AnchorModel,
  getMediaType,
} from "common247";

/**
 * Safely converts an input into an array, handling arrays, objects/maps, and null/undefined.
 */
export function toSafeArray<T>(val: any): T[] {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === "object") return Object.values(val) as T[];
  return [];
}

/**
 * Safely extracts a Set of string IDs from arrays, sets, or object keys/values.
 */
export function toSafeIdSet(val: any): Set<string> {
  if (!val) return new Set<string>();
  if (Array.isArray(val)) return new Set<string>(val.filter(Boolean).map(String));
  if (val instanceof Set) return new Set<string>(Array.from(val).filter(Boolean).map(String));
  if (typeof val === "object") {
    return new Set<string>(Object.keys(val).filter(Boolean).map(String));
  }
  return new Set<string>();
}

/**
 * Parses date string, number, or Date into a normalized Unix epoch timestamp.
 */
export function parseDateToEpoch(val: any): number | null {
  if (!val) return null;
  if (typeof val === "number") {
    // 4-digit year like 2026
    if (val > 1900 && val < 2100) {
      return new Date(val, 0, 1).getTime();
    }
    return val;
  }
  if (val instanceof Date) return val.getTime();

  const str = String(val).trim();
  if (/^\d{4}$/.test(str)) {
    return new Date(parseInt(str, 10), 0, 1).getTime();
  }

  const parsed = Date.parse(str);
  if (!isNaN(parsed)) return parsed;

  const parts = str.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})/);
  if (parts) {
    const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
    const mIdx = months.indexOf(parts[2].toLowerCase());
    if (mIdx !== -1) {
      return new Date(parseInt(parts[3], 10), mIdx, parseInt(parts[1], 10)).getTime();
    }
  }

  return null;
}

/**
 * Resolves the primary search query from the entity-specific tab, global query, or other active tab queries.
 */
export function getEffectiveQuery(
  primaryQuery?: string,
  globalQuery?: string,
  ...otherQueries: Array<string | undefined>
): string {
  const p = (primaryQuery || "").trim().toLowerCase();
  if (p) return p;
  const g = (globalQuery || "").trim().toLowerCase();
  if (g) return g;
  for (const o of otherQueries) {
    const trimmed = (o || "").trim().toLowerCase();
    if (trimmed) return trimmed;
  }
  return "";
}

/**
 * Calculates effective date range boundaries across multiple sub-filter criteria.
 * Returns { fromEpoch, toEpoch } where fromEpoch is the most restrictive start date
 * and toEpoch is the most restrictive end date.
 */
export function getEffectiveDateRange(
  ...ranges: Array<{ from?: any; to?: any } | undefined>
): { fromEpoch: number | null; toEpoch: number | null } {
  let fromEpoch: number | null = null;
  let toEpoch: number | null = null;
  for (const r of ranges) {
    if (!r) continue;
    const f = parseDateToEpoch(r.from);
    if (f !== null) {
      fromEpoch = fromEpoch === null ? f : Math.max(fromEpoch, f);
    }
    const t = parseDateToEpoch(r.to);
    if (t !== null) {
      toEpoch = toEpoch === null ? t : Math.min(toEpoch, t);
    }
  }
  return { fromEpoch, toEpoch };
}

/**
 * Creates an empty initial Unified Filter payload.
 */
export function createDefaultFilter(): IUnifiedFilter {
  return {
    searchQuery: "",
    memoryFilter: {},
    mediaFilter: {},
    contactFilter: {},
    groupFilter: {},
    tagFilter: {},
    shareFilter: {},
    activityFilter: {},
    messageFilter: {},
    placeFilter: {},
    anchorFilter: {},
  };
}

/**
 * Calculates the total number of distinct active filter criteria across all child filter blocks.
 */
export function countActiveFilterRules(filter?: IUnifiedFilter): number {
  if (!filter) return 0;
  let count = 0;

  if (filter.searchQuery && filter.searchQuery.trim().length > 0) count++;

  // Memory Filter
  const mf = filter.memoryFilter;
  if (mf) {
    if (mf.searchQuery && mf.searchQuery.trim().length > 0) count++;
    if (mf.dateFrom) count++;
    if (mf.dateTo) count++;
    if (mf.contactIds && mf.contactIds.length > 0) count++;
    if (mf.groupIds && mf.groupIds.length > 0) count++;
    if (mf.tagIds && mf.tagIds.length > 0) count++;
    if (mf.placeIds && mf.placeIds.length > 0) count++;
    if (mf.anchorIds && mf.anchorIds.length > 0) count++;
    if (mf.mediaTypes && mf.mediaTypes.length > 0 && !mf.mediaTypes.includes("all")) count++;
    if (mf.minRating && mf.minRating > 1) count++;
    if (mf.onlyWithMedia) count++;
    if (mf.memoryType) count++;
  }

  // Media Filter
  const medF = filter.mediaFilter;
  if (medF) {
    if (medF.searchQuery && medF.searchQuery.trim().length > 0) count++;
    if (medF.dateFrom) count++;
    if (medF.dateTo) count++;
    if (medF.mediaTypes && medF.mediaTypes.length > 0 && !medF.mediaTypes.includes("all")) count++;
    if (medF.category && medF.category !== "all") count++;
    if (medF.minRating && medF.minRating > 1) count++;
    if (medF.contactIds && medF.contactIds.length > 0) count++;
    if (medF.groupIds && medF.groupIds.length > 0) count++;
    if (medF.tagIds && medF.tagIds.length > 0) count++;
    if (medF.memoryIds && medF.memoryIds.length > 0) count++;
    if (medF.placeIds && medF.placeIds.length > 0) count++;
    if (medF.anchorIds && medF.anchorIds.length > 0) count++;
  }

  // Contact Filter
  const cf = filter.contactFilter;
  if (cf) {
    if (cf.searchQuery && cf.searchQuery.trim().length > 0) count++;
    if (cf.dateFrom) count++;
    if (cf.dateTo) count++;
    if (cf.userRoles && cf.userRoles.length > 0) count++;
    if (cf.isLinked !== undefined) count++;
    if (cf.groupIds && cf.groupIds.length > 0) count++;
    if (cf.tagIds && cf.tagIds.length > 0) count++;
    if (cf.anchorIds && cf.anchorIds.length > 0) count++;
  }

  // Group Filter
  const gf = filter.groupFilter;
  if (gf) {
    if (gf.searchQuery && gf.searchQuery.trim().length > 0) count++;
    if (gf.contactIds && gf.contactIds.length > 0) count++;
    if (gf.tagIds && gf.tagIds.length > 0) count++;
    if (gf.minMemberCount && gf.minMemberCount > 0) count++;
    if (gf.anchorIds && gf.anchorIds.length > 0) count++;
  }

  // Tag Filter
  const tf = filter.tagFilter;
  if (tf) {
    if (tf.tagIds && tf.tagIds.length > 0) count++;
    if (tf.tagTypes && tf.tagTypes.length > 0) count++;
  }

  // Share Filter
  const sf = filter.shareFilter;
  if (sf) {
    if (sf.searchQuery && sf.searchQuery.trim().length > 0) count++;
    if (sf.roles && sf.roles.length > 0) count++;
    if (sf.sourceType) count++;
    if (sf.direction && sf.direction !== "all") count++;
    if (sf.targetUserIds && sf.targetUserIds.length > 0) count++;
  }

  // Activity Filter
  const af = filter.activityFilter;
  if (af) {
    if (af.searchQuery && af.searchQuery.trim().length > 0) count++;
    if (af.dateFrom) count++;
    if (af.dateTo) count++;
    if (af.actions && af.actions.length > 0) count++;
    if (af.entityTypes && af.entityTypes.length > 0) count++;
    if (af.actorUserIds && af.actorUserIds.length > 0) count++;
  }

  // Message Filter
  const msgF = filter.messageFilter;
  if (msgF) {
    if (msgF.searchQuery && msgF.searchQuery.trim().length > 0) count++;
    if (msgF.folder && msgF.folder !== "all") count++;
    if (msgF.readStatus && msgF.readStatus !== "all") count++;
    if (msgF.contactIds && msgF.contactIds.length > 0) count++;
  }

  // Place Filter
  const pf = filter.placeFilter;
  if (pf) {
    if (pf.searchQuery && pf.searchQuery.trim().length > 0) count++;
    if (pf.dateFrom) count++;
    if (pf.dateTo) count++;
    if (pf.minRating && pf.minRating > 1) count++;
    if (pf.placeIds && pf.placeIds.length > 0) count++;
    if (pf.memoryIds && pf.memoryIds.length > 0) count++;
    if (pf.mediaIds && pf.mediaIds.length > 0) count++;
    if (pf.contactIds && pf.contactIds.length > 0) count++;
    if (pf.groupIds && pf.groupIds.length > 0) count++;
    if (pf.tagIds && pf.tagIds.length > 0) count++;
    if (pf.anchorIds && pf.anchorIds.length > 0) count++;
  }

  // Anchor Filter
  const ancF = filter.anchorFilter;
  if (ancF) {
    if ((ancF.searchQuery && ancF.searchQuery.trim().length > 0) || (ancF.query && ancF.query.trim().length > 0)) count++;
    if (ancF.dateFrom || ancF.dateRange?.from) count++;
    if (ancF.dateTo || ancF.dateRange?.to) count++;
    if (ancF.types && ancF.types.length > 0) count++;
    if (ancF.anchorIds && ancF.anchorIds.length > 0) count++;
    if (ancF.memoryIds && ancF.memoryIds.length > 0) count++;
    if (ancF.mediaIds && ancF.mediaIds.length > 0) count++;
    if (ancF.contactIds && ancF.contactIds.length > 0) count++;
    if (ancF.groupIds && ancF.groupIds.length > 0) count++;
    if (ancF.placeIds && ancF.placeIds.length > 0) count++;
    if (ancF.tagIds && ancF.tagIds.length > 0) count++;
    if (ancF.hasLocation !== undefined) count++;
  }

  return count;
}

/**
 * Checks whether any filter criteria is active.
 */
export function isFilterActive(filter?: IUnifiedFilter): boolean {
  return countActiveFilterRules(filter) > 0;
}

/**
 * Evaluates memories against the unified filter criteria.
 */
export function filterMemories(memories: MemoryModel[] = [], filter: IUnifiedFilter = {}): MemoryModel[] {
  const list = toSafeArray<MemoryModel>(memories);
  if (!isFilterActive(filter)) return list;

  const mf = filter.memoryFilter || {};
  const effectiveQuery = getEffectiveQuery(
    mf.searchQuery,
    filter.searchQuery,
    filter.mediaFilter?.searchQuery,
    filter.contactFilter?.searchQuery,
    filter.groupFilter?.searchQuery
  );

  const { fromEpoch, toEpoch } = getEffectiveDateRange(
    { from: mf.dateFrom, to: mf.dateTo },
    { from: filter.mediaFilter?.dateFrom, to: filter.mediaFilter?.dateTo },
    { from: filter.contactFilter?.dateFrom, to: filter.contactFilter?.dateTo }
  );

  const contactIds = toSafeIdSet([
    ...(mf.contactIds || []),
    ...(filter.mediaFilter?.contactIds || []),
    ...(filter.groupFilter?.contactIds || []),
    ...(filter.messageFilter?.contactIds || []),
  ]);
  const groupIds = toSafeIdSet([
    ...(mf.groupIds || []),
    ...(filter.mediaFilter?.groupIds || []),
    ...(filter.contactFilter?.groupIds || []),
  ]);
  const tagIds = toSafeIdSet([
    ...(mf.tagIds || []),
    ...(filter.tagFilter?.tagIds || []),
    ...(filter.mediaFilter?.tagIds || []),
    ...(filter.contactFilter?.tagIds || []),
    ...(filter.groupFilter?.tagIds || []),
  ]);

  const allowedMediaTypes = [
    ...(mf.mediaTypes || []),
    ...(filter.mediaFilter?.mediaTypes || []),
  ].filter((t) => t && t !== "all");

  const minRating = Math.max(mf.minRating || 1, filter.mediaFilter?.minRating || 1);
  const onlyWithMedia = Boolean(
    mf.onlyWithMedia ||
    (filter.mediaFilter?.mediaTypes && filter.mediaFilter.mediaTypes.length > 0 && !filter.mediaFilter.mediaTypes.includes("all"))
  );

  return list.filter((mem) => {
    if (!mem) return false;

    // 1. Text Search
    if (effectiveQuery) {
      const titleMatch = (mem.title || "").toLowerCase().includes(effectiveQuery);
      const descMatch = (mem.description || "").toLowerCase().includes(effectiveQuery);
      const locText = typeof mem.location === "string" ? mem.location : (mem.location?.text || "");
      const locMatch = locText.toLowerCase().includes(effectiveQuery);
      if (!titleMatch && !descMatch && !locMatch) return false;
    }

    // 2. Date Range
    const epoch = parseDateToEpoch(mem.date) || mem.createdAt || null;
    if (epoch) {
      if (fromEpoch !== null && epoch < fromEpoch) return false;
      if (toEpoch !== null && epoch > toEpoch) return false;
    }

    // 3. Contact Filter
    if (contactIds.size > 0) {
      const memContactIds: string[] = [];
      if (Array.isArray(mem.contactIds)) memContactIds.push(...mem.contactIds);
      if (mem.contacts && typeof mem.contacts === "object") {
        memContactIds.push(...Object.keys(mem.contacts));
      }
      const hasContactMatch = memContactIds.some((id) => contactIds.has(id));
      if (!hasContactMatch) return false;
    }

    // 4. Group Filter
    if (groupIds.size > 0) {
      const memGroupIds: string[] = [];
      if (Array.isArray(mem.groupIds)) memGroupIds.push(...mem.groupIds);
      if (mem.groups && typeof mem.groups === "object") {
        memGroupIds.push(...Object.keys(mem.groups));
      }
      const hasGroupMatch = memGroupIds.some((id) => groupIds.has(id));
      if (!hasGroupMatch) return false;
    }

    // 5. Tag Filter
    if (tagIds.size > 0) {
      const memTagIds: string[] = [];
      if (Array.isArray(mem.tagIds)) memTagIds.push(...mem.tagIds);
      if (mem.tags && typeof mem.tags === "object") {
        memTagIds.push(...Object.keys(mem.tags));
      }
      const hasTagMatch = memTagIds.some((id) => tagIds.has(id));
      if (!hasTagMatch) return false;
    }

    // 6. Rating Filter
    if (minRating > 1) {
      const rating = mem.favouriteRating ?? (mem as any).favouriteRate ?? 1;
      if (rating < minRating) return false;
    }

    // 7. Only with media
    const mediaList = toSafeArray(mem.media || mem.mediaItems);
    const mediaCount = (mem.mediaIds?.length || 0) + mediaList.length;
    if (onlyWithMedia && mediaCount === 0) {
      return false;
    }

    // 8. Media Types Filter
    if (allowedMediaTypes.length > 0) {
      const mediaItems = toSafeArray<MediaModel>(mem.mediaItems || (mem.media ? Object.values(mem.media) : []));
      if (mediaItems.length === 0) return false;
      const hasMatchingType = mediaItems.some((m) => {
        const type = getMediaType((m as any).contentType || (m as any).mimeType, (m as any).fileName || m.title);
        return allowedMediaTypes.includes(type);
      });
      if (!hasMatchingType) return false;
    }

    // 9. Memory Type
    if (mf.memoryType && (mem as any).type && (mem as any).type !== mf.memoryType) {
      return false;
    }

    return true;
  });
}

/**
 * Evaluates media against the unified filter criteria.
 */
export function filterMedia(media: MediaModel[] = [], filter: IUnifiedFilter = {}): MediaModel[] {
  const list = toSafeArray<MediaModel>(media);
  if (!isFilterActive(filter)) return list;

  const medF = filter.mediaFilter || {};
  const effectiveQuery = getEffectiveQuery(
    medF.searchQuery,
    filter.searchQuery,
    filter.memoryFilter?.searchQuery,
    filter.contactFilter?.searchQuery,
    filter.groupFilter?.searchQuery
  );

  const { fromEpoch, toEpoch } = getEffectiveDateRange(
    { from: medF.dateFrom, to: medF.dateTo },
    { from: filter.memoryFilter?.dateFrom, to: filter.memoryFilter?.dateTo },
    { from: filter.contactFilter?.dateFrom, to: filter.contactFilter?.dateTo }
  );

  const contactIds = toSafeIdSet([
    ...(medF.contactIds || []),
    ...(filter.memoryFilter?.contactIds || []),
    ...(filter.groupFilter?.contactIds || []),
  ]);
  const groupIds = toSafeIdSet([
    ...(medF.groupIds || []),
    ...(filter.memoryFilter?.groupIds || []),
    ...(filter.contactFilter?.groupIds || []),
  ]);
  const memoryIds = toSafeIdSet(medF.memoryIds);
  const tagIds = toSafeIdSet([
    ...(medF.tagIds || []),
    ...(filter.tagFilter?.tagIds || []),
    ...(filter.memoryFilter?.tagIds || []),
    ...(filter.contactFilter?.tagIds || []),
  ]);

  const allowedTypes = [
    ...(medF.mediaTypes || []),
    ...(filter.memoryFilter?.mediaTypes || []),
  ].filter((t) => t && t !== "all");

  const category = medF.category && medF.category !== "all" ? medF.category : undefined;
  const minRating = Math.max(medF.minRating || 1, filter.memoryFilter?.minRating || 1);

  return list.filter((m) => {
    if (!m) return false;

    // 1. Text search
    if (effectiveQuery) {
      const titleMatch = (m.title || (m as any).name || "").toLowerCase().includes(effectiveQuery);
      const descMatch = (m.description || "").toLowerCase().includes(effectiveQuery);
      const urlMatch = (m.downloadUrl || "").toLowerCase().includes(effectiveQuery);
      if (!titleMatch && !descMatch && !urlMatch) return false;
    }

    // 2. Date Range
    const epoch = parseDateToEpoch(m.date) || m.createdAt || null;
    if (epoch) {
      if (fromEpoch !== null && epoch < fromEpoch) return false;
      if (toEpoch !== null && epoch > toEpoch) return false;
    }

    // 3. Media Type
    if (allowedTypes.length > 0) {
      const type = getMediaType(m.contentType || (m as any).mimeType, (m as any).fileName || m.title);
      if (!allowedTypes.includes(type)) return false;
    }

    // 4. Category
    if (category) {
      const cat = m.mediaCategory || (m as any).category;
      if (cat !== category) return false;
    }

    // 5. Rating
    if (minRating > 1) {
      const rating = m.favouriteRating ?? (m as any).favouriteRate ?? 1;
      if (rating < minRating) return false;
    }

    // 6. Memory IDs
    if (memoryIds.size > 0) {
      const memIds = m.memoryIds || (m.memories ? Object.keys(m.memories) : []);
      if (!memIds.some((id) => memoryIds.has(id))) return false;
    }

    // 7. Contact IDs
    if (contactIds.size > 0) {
      const cIds = m.contactIds || (m.contacts ? Object.keys(m.contacts) : []);
      if (!cIds.some((id) => contactIds.has(id))) return false;
    }

    // 8. Group IDs
    if (groupIds.size > 0) {
      const gIds = m.groupIds || (m.groups ? Object.keys(m.groups) : []);
      if (!gIds.some((id) => groupIds.has(id))) return false;
    }

    // 9. Tag IDs
    if (tagIds.size > 0) {
      const tIds = m.tagIds || (m.tags ? Object.keys(m.tags) : []);
      if (!tIds.some((id) => tagIds.has(id))) return false;
    }

    return true;
  });
}

/**
 * Evaluates contacts against the unified filter criteria.
 */
export function filterContacts(
  contacts: (Contact | ContactModel)[] = [],
  filter: IUnifiedFilter = {}
): (Contact | ContactModel)[] {
  const list = toSafeArray<Contact | ContactModel>(contacts);
  if (!isFilterActive(filter)) return list;

  const cf = filter.contactFilter || {};
  const effectiveQuery = getEffectiveQuery(
    cf.searchQuery,
    filter.searchQuery,
    filter.memoryFilter?.searchQuery,
    filter.groupFilter?.searchQuery
  );

  const { fromEpoch, toEpoch } = getEffectiveDateRange(
    { from: cf.dateFrom, to: cf.dateTo },
    { from: filter.memoryFilter?.dateFrom, to: filter.memoryFilter?.dateTo },
    { from: filter.mediaFilter?.dateFrom, to: filter.mediaFilter?.dateTo }
  );

  const explicitContactIds = toSafeIdSet([
    ...(cf.contactIds || []),
    ...(filter.memoryFilter?.contactIds || []),
    ...(filter.mediaFilter?.contactIds || []),
    ...(filter.groupFilter?.contactIds || []),
  ]);

  const groupIds = toSafeIdSet([
    ...(cf.groupIds || []),
    ...(filter.memoryFilter?.groupIds || []),
    ...(filter.mediaFilter?.groupIds || []),
  ]);
  const tagIds = toSafeIdSet([
    ...(cf.tagIds || []),
    ...(filter.tagFilter?.tagIds || []),
    ...(filter.memoryFilter?.tagIds || []),
    ...(filter.mediaFilter?.tagIds || []),
  ]);
  const isLinked = cf.isLinked;
  const userRoles = cf.userRoles;

  return list.filter((c) => {
    if (!c) return false;

    // 1. Text Search
    if (effectiveQuery) {
      const nameMatch = (c.displayName || "").toLowerCase().includes(effectiveQuery);
      const emailMatch = (c.email || "").toLowerCase().includes(effectiveQuery);
      const descMatch = (c.description || "").toLowerCase().includes(effectiveQuery);
      if (!nameMatch && !emailMatch && !descMatch) return false;
    }

    // 2. Explicit Contact Selection across any tab
    if (explicitContactIds.size > 0) {
      if (!c.id || !explicitContactIds.has(c.id)) return false;
    }

    // 3. Date Range
    if (c.createdAt) {
      if (fromEpoch !== null && c.createdAt < fromEpoch) return false;
      if (toEpoch !== null && c.createdAt > toEpoch) return false;
    }

    // 4. Group IDs
    if (groupIds.size > 0) {
      const cGroupIds = c.groupIds || (c.groups ? Object.keys(c.groups) : []);
      if (!cGroupIds.some((id) => groupIds.has(id))) return false;
    }

    // 5. Tag IDs
    if (tagIds.size > 0) {
      const cTagIds = c.tagIds || (c.tags ? Object.keys(c.tags) : []);
      if (!cTagIds.some((id) => tagIds.has(id))) return false;
    }

    // 6. Linked state
    if (isLinked !== undefined) {
      const linked = Boolean(c.userId || (c as any).isLinked);
      if (linked !== isLinked) return false;
    }

    // 7. User Roles
    if (userRoles && userRoles.length > 0) {
      const role = (c as any).userRole || (c as any).role || "viewer";
      if (!userRoles.includes(role)) return false;
    }

    return true;
  });
}

/**
 * Evaluates groups against the unified filter criteria.
 */
export function filterGroups(groups: GroupModel[] = [], filter: IUnifiedFilter = {}): GroupModel[] {
  const list = toSafeArray<GroupModel>(groups);
  if (!isFilterActive(filter)) return list;

  const gf = filter.groupFilter || {};
  const effectiveQuery = getEffectiveQuery(
    gf.searchQuery,
    filter.searchQuery,
    filter.memoryFilter?.searchQuery,
    filter.contactFilter?.searchQuery
  );

  const explicitGroupIds = toSafeIdSet([
    ...(gf.groupIds || []),
    ...(filter.memoryFilter?.groupIds || []),
    ...(filter.mediaFilter?.groupIds || []),
    ...(filter.contactFilter?.groupIds || []),
  ]);

  const contactIds = toSafeIdSet([
    ...(gf.contactIds || []),
    ...(filter.memoryFilter?.contactIds || []),
    ...(filter.mediaFilter?.contactIds || []),
  ]);
  const tagIds = toSafeIdSet([
    ...(gf.tagIds || []),
    ...(filter.tagFilter?.tagIds || []),
    ...(filter.memoryFilter?.tagIds || []),
    ...(filter.contactFilter?.tagIds || []),
  ]);
  const minMemberCount = gf.minMemberCount || 0;

  return list.filter((g) => {
    if (!g) return false;

    // 1. Text Search
    if (effectiveQuery) {
      const nameMatch = (g.name || "").toLowerCase().includes(effectiveQuery);
      const descMatch = (g.description || "").toLowerCase().includes(effectiveQuery);
      if (!nameMatch && !descMatch) return false;
    }

    // 2. Explicit Group Selection across any tab
    if (explicitGroupIds.size > 0) {
      if (!g.id || !explicitGroupIds.has(g.id)) return false;
    }

    // 3. Contact membership
    if (contactIds.size > 0) {
      const gContactIds = g.contactIds || (g.contacts ? Object.keys(g.contacts) : []);
      if (!gContactIds.some((id) => contactIds.has(id))) return false;
    }

    // 4. Tag IDs
    if (tagIds.size > 0) {
      const gTagIds = g.tagIds || (g.tags ? Object.keys(g.tags) : []);
      if (!gTagIds.some((id) => tagIds.has(id))) return false;
    }

    // 5. Min members
    if (minMemberCount > 0) {
      const contactCount = (g.contactIds && g.contactIds.length > 0)
        ? g.contactIds.length
        : (g.contacts ? Object.keys(g.contacts).length : 0);
      const count = contactCount + ((g as any).memberCount || 0);
      if (count < minMemberCount) return false;
    }

    return true;
  });
}

/**
 * Evaluates shares against the unified filter criteria.
 */
export function filterShares(shares: ShareModel[] = [], filter: IUnifiedFilter = {}): ShareModel[] {
  const list = toSafeArray<ShareModel>(shares);
  if (!isFilterActive(filter)) return list;

  const globalQuery = (filter.searchQuery || "").toLowerCase().trim();
  const sf = filter.shareFilter || {};
  const shareQuery = (sf.searchQuery || "").toLowerCase().trim();
  const effectiveQuery = shareQuery || globalQuery;

  const roles = sf.roles;
  const sourceType = sf.sourceType;
  const direction = sf.direction;
  const targetUserIds = toSafeIdSet(sf.targetUserIds);

  return list.filter((s) => {
    if (!s) return false;

    // 1. Text search
    if (effectiveQuery) {
      const targetMatch = (s.targetDisplayName || "").toLowerCase().includes(effectiveQuery);
      const sourceMatch = (s.sourceDisplayName || "").toLowerCase().includes(effectiveQuery);
      const sharerMatch = (s.sharedByDisplayName || "").toLowerCase().includes(effectiveQuery);
      if (!targetMatch && !sourceMatch && !sharerMatch) return false;
    }

    // 2. Role
    if (roles && roles.length > 0) {
      if (!roles.includes(s.role as any)) return false;
    }

    // 3. Source Type
    if (sourceType && s.sourceType !== sourceType) {
      return false;
    }

    // 4. Direction
    if (direction && direction !== "all") {
      const isSharedByMe = (s as any).isSharedByMe ?? (s.direction === "shared_by_me");
      if (direction === "shared_by_me" && !isSharedByMe) return false;
      if (direction === "shared_with_me" && isSharedByMe) return false;
    }

    // 5. Target User
    if (targetUserIds.size > 0) {
      if (!s.targetUserId || !targetUserIds.has(s.targetUserId)) return false;
    }

    return true;
  });
}

/**
 * Evaluates activity logs against the unified filter criteria.
 */
export function filterActivities(
  activities: ActivityLogModel[] = [],
  filter: IUnifiedFilter = {}
): ActivityLogModel[] {
  const list = toSafeArray<ActivityLogModel>(activities);
  if (!isFilterActive(filter)) return list;

  const globalQuery = (filter.searchQuery || "").toLowerCase().trim();
  const af = filter.activityFilter || {};
  const actQuery = (af.searchQuery || "").toLowerCase().trim();
  const effectiveQuery = actQuery || globalQuery;

  const fromEpoch = parseDateToEpoch(af.dateFrom);
  const toEpoch = parseDateToEpoch(af.dateTo);
  const actions = af.actions;
  const entityTypes = af.entityTypes;
  const actorUserIds = toSafeIdSet(af.actorUserIds);

  return list.filter((act) => {
    if (!act) return false;

    // 1. Text search
    if (effectiveQuery) {
      const text = `${act.headline || ""} ${act.details || ""} ${act.targetTitle || ""} ${act.actorName || ""}`.toLowerCase();
      if (!text.includes(effectiveQuery)) return false;
    }

    // 2. Date Range
    if (act.timestamp) {
      if (fromEpoch !== null && act.timestamp < fromEpoch) return false;
      if (toEpoch !== null && act.timestamp > toEpoch) return false;
    }

    // 3. Actions
    if (actions && actions.length > 0) {
      if (!actions.includes(act.action as any)) return false;
    }

    // 4. Target Entity Type
    if (entityTypes && entityTypes.length > 0) {
      if (!entityTypes.includes(act.targetType)) return false;
    }

    // 5. Actor User IDs
    if (actorUserIds.size > 0) {
      if (!act.actorUserId || !actorUserIds.has(act.actorUserId)) return false;
    }

    return true;
  });
}

/**
 * Evaluates messages against the unified filter criteria.
 */
export function filterMessages(messages: MessageModel[] = [], filter: IUnifiedFilter = {}): MessageModel[] {
  const list = toSafeArray<MessageModel>(messages);
  if (!isFilterActive(filter)) return list;

  const globalQuery = (filter.searchQuery || "").toLowerCase().trim();
  const msgF = filter.messageFilter || {};
  const messageQuery = (msgF.searchQuery || "").toLowerCase().trim();
  const effectiveQuery = messageQuery || globalQuery;

  const folder = msgF.folder;
  const readStatus = msgF.readStatus;
  const contactIds = toSafeIdSet(msgF.contactIds);

  return list.filter((msg) => {
    if (!msg) return false;

    // 1. Text search
    if (effectiveQuery) {
      const subjectMatch = (msg.subject || "").toLowerCase().includes(effectiveQuery);
      const bodyMatch = (msg.body || "").toLowerCase().includes(effectiveQuery);
      const senderMatch = (msg.senderDisplayName || "").toLowerCase().includes(effectiveQuery);
      const targetMatch = (msg.targetName || "").toLowerCase().includes(effectiveQuery);
      if (!subjectMatch && !bodyMatch && !senderMatch && !targetMatch) return false;
    }

    // 2. Folder
    if (folder && folder !== "all") {
      if (folder === "trash" && !msg.isTrashed) return false;
      if (folder === "sent" && !msg.isSent) return false;
      if (folder === "inbox" && (msg.isTrashed || msg.isSent)) return false;
    }

    // 3. Read status
    if (readStatus && readStatus !== "all") {
      if (readStatus === "read" && !msg.isRead) return false;
      if (readStatus === "unread" && msg.isRead) return false;
    }

    // 4. Contacts
    if (contactIds.size > 0) {
      const senderMatch = msg.senderContactId && contactIds.has(msg.senderContactId);
      const recMatch = msg.recipientContactIds?.some((id) => contactIds.has(id));
      if (!senderMatch && !recMatch) return false;
    }

    return true;
  });
}

/**
 * Evaluates places against the unified filter criteria.
 */
export function filterPlaces(
  places: PlaceModel[] = [],
  filter: IUnifiedFilter = {}
): PlaceModel[] {
  const list = toSafeArray<PlaceModel>(places);
  if (!isFilterActive(filter)) return list;

  const pf = filter.placeFilter || {};
  const effectiveQuery = getEffectiveQuery(
    pf.searchQuery,
    filter.searchQuery,
    filter.memoryFilter?.searchQuery
  );

  const { fromEpoch, toEpoch } = getEffectiveDateRange(
    { from: pf.dateFrom, to: pf.dateTo },
    { from: filter.memoryFilter?.dateFrom, to: filter.memoryFilter?.dateTo },
    { from: filter.mediaFilter?.dateFrom, to: filter.mediaFilter?.dateTo }
  );

  const explicitPlaceIds = toSafeIdSet([
    ...(pf.placeIds || []),
    ...(filter.memoryFilter?.placeIds || []),
  ]);

  const contactIds = toSafeIdSet([
    ...(pf.contactIds || []),
    ...(filter.memoryFilter?.contactIds || []),
  ]);

  const groupIds = toSafeIdSet([
    ...(pf.groupIds || []),
    ...(filter.memoryFilter?.groupIds || []),
  ]);

  const memoryIds = toSafeIdSet(pf.memoryIds);
  const mediaIds = toSafeIdSet(pf.mediaIds);
  const tagIds = toSafeIdSet([
    ...(pf.tagIds || []),
    ...(filter.tagFilter?.tagIds || []),
  ]);

  const minRating = pf.minRating || 1;

  return list.filter((p) => {
    if (!p) return false;

    // 1. Text Search
    if (effectiveQuery) {
      const nameMatch = (p.name || "").toLowerCase().includes(effectiveQuery);
      const descMatch = (p.description || "").toLowerCase().includes(effectiveQuery);
      const addrMatch = (p.address || "").toLowerCase().includes(effectiveQuery);
      if (!nameMatch && !descMatch && !addrMatch) return false;
    }

    // 2. Explicit Place Selection
    if (explicitPlaceIds.size > 0) {
      if (!p.id || !explicitPlaceIds.has(p.id)) return false;
    }

    // 3. Date Range
    const dateVal = typeof p.date === "object" ? ((p.date as any)?.from || (p.date as any)?.formatted) : p.date;
    const epoch = parseDateToEpoch(dateVal) || p.createdAt || null;
    if (epoch) {
      if (fromEpoch !== null && epoch < fromEpoch) return false;
      if (toEpoch !== null && epoch > toEpoch) return false;
    }

    // 4. Rating
    if (minRating > 1) {
      const rating = p.favouriteRating ?? 1;
      if (rating < minRating) return false;
    }

    // 5. Memory IDs
    if (memoryIds.size > 0) {
      const memIds = p.memoryIds || (p.memories ? Object.keys(p.memories) : []);
      if (!memIds.some((id) => memoryIds.has(id))) return false;
    }

    // 6. Media IDs
    if (mediaIds.size > 0) {
      const medIds = p.mediaIds || (p.media ? Object.keys(p.media) : []);
      if (!medIds.some((id) => mediaIds.has(id))) return false;
    }

    // 7. Contact IDs
    if (contactIds.size > 0) {
      const cIds = p.contactIds || (p.contacts ? Object.keys(p.contacts) : []);
      if (!cIds.some((id) => contactIds.has(id))) return false;
    }

    // 8. Group IDs
    if (groupIds.size > 0) {
      const gIds = p.groupIds || (p.groups ? Object.keys(p.groups) : []);
      if (!gIds.some((id) => groupIds.has(id))) return false;
    }

    // 9. Tag IDs
    if (tagIds.size > 0) {
      const tIds = p.tagIds || (p.tags ? Object.keys(p.tags) : []);
      if (!tIds.some((id) => tagIds.has(id))) return false;
    }

    // 10. Anchor IDs
    const anchorIds = toSafeIdSet([
      ...(pf.anchorIds || []),
      ...(filter.anchorFilter?.anchorIds || []),
    ]);
    if (anchorIds.size > 0) {
      const aIds = p.anchorIds || (p.anchors ? Object.keys(p.anchors) : []);
      if (!aIds.some((id) => anchorIds.has(id))) return false;
    }

    return true;
  });
}

/**
 * Evaluates anchors against the unified filter criteria.
 */
export function filterAnchors(
  anchors: AnchorModel[] = [],
  filter: IUnifiedFilter = {}
): AnchorModel[] {
  const list = toSafeArray<AnchorModel>(anchors);
  if (!isFilterActive(filter)) return list;

  const af = filter.anchorFilter || {};
  const effectiveQuery = getEffectiveQuery(
    af.searchQuery || af.query,
    filter.searchQuery,
    filter.memoryFilter?.searchQuery
  );

  const { fromEpoch, toEpoch } = getEffectiveDateRange(
    { from: af.dateFrom || af.dateRange?.from, to: af.dateTo || af.dateRange?.to },
    { from: filter.memoryFilter?.dateFrom, to: filter.memoryFilter?.dateTo },
    { from: filter.mediaFilter?.dateFrom, to: filter.mediaFilter?.dateTo }
  );

  const explicitAnchorIds = toSafeIdSet([
    ...(af.anchorIds || []),
    ...(filter.memoryFilter?.anchorIds || []),
  ]);

  const contactIds = toSafeIdSet([
    ...(af.contactIds || []),
    ...(filter.memoryFilter?.contactIds || []),
  ]);

  const groupIds = toSafeIdSet([
    ...(af.groupIds || []),
    ...(filter.memoryFilter?.groupIds || []),
  ]);

  const placeIds = toSafeIdSet([
    ...(af.placeIds || []),
    ...(filter.placeFilter?.placeIds || []),
  ]);

  const memoryIds = toSafeIdSet(af.memoryIds);
  const mediaIds = toSafeIdSet(af.mediaIds);
  const tagIds = toSafeIdSet([
    ...(af.tagIds || []),
    ...(filter.tagFilter?.tagIds || []),
  ]);

  const types = af.types && af.types.length > 0 ? new Set(af.types) : null;
  const hasLocation = af.hasLocation;

  return list.filter((a) => {
    if (!a) return false;

    // 1. Text Search (Guarded against null/undefined trim crashes)
    if (effectiveQuery) {
      const nameMatch = (a.name || "").toLowerCase().includes(effectiveQuery);
      const descMatch = (a.description || "").toLowerCase().includes(effectiveQuery);
      const addrMatch = (a.address || "").toLowerCase().includes(effectiveQuery);
      const typeMatch = (a.type || "").toLowerCase().includes(effectiveQuery);
      if (!nameMatch && !descMatch && !addrMatch && !typeMatch) return false;
    }

    // 2. Explicit Anchor Selection
    if (explicitAnchorIds.size > 0) {
      if (!a.id || !explicitAnchorIds.has(a.id)) return false;
    }

    // 3. Types
    if (types && !types.has(a.type)) {
      return false;
    }

    // 4. Date Range
    const anchorDateVal = typeof a.date === "object" ? ((a.date as any)?.from || (a.date as any)?.formatted) : a.date;
    const epoch = parseDateToEpoch(anchorDateVal) || a.createdAt || null;
    if (epoch) {
      if (fromEpoch !== null && epoch < fromEpoch) return false;
      if (toEpoch !== null && epoch > toEpoch) return false;
    }

    // 5. Has Location
    if (hasLocation !== undefined) {
      const itemHasLoc = Boolean(a.location?.latitude && a.location?.longitude);
      if (hasLocation !== itemHasLoc) return false;
    }

    // 6. Memory IDs
    if (memoryIds.size > 0) {
      const memIds = a.memoryIds || (a.memories ? Object.keys(a.memories) : []);
      if (!memIds.some((id) => memoryIds.has(id))) return false;
    }

    // 7. Media IDs
    if (mediaIds.size > 0) {
      const medIds = a.mediaIds || (a.media ? Object.keys(a.media) : []);
      if (!medIds.some((id) => mediaIds.has(id))) return false;
    }

    // 8. Contact IDs
    if (contactIds.size > 0) {
      const cIds = a.contactIds || (a.contacts ? Object.keys(a.contacts) : []);
      if (!cIds.some((id) => contactIds.has(id))) return false;
    }

    // 9. Group IDs
    if (groupIds.size > 0) {
      const gIds = a.groupIds || (a.groups ? Object.keys(a.groups) : []);
      if (!gIds.some((id) => groupIds.has(id))) return false;
    }

    // 10. Place IDs
    if (placeIds.size > 0) {
      const pIds = a.placeIds || (a.places ? Object.keys(a.places) : []);
      if (!pIds.some((id) => placeIds.has(id))) return false;
    }

    // 11. Tag IDs
    if (tagIds.size > 0) {
      const tIds = a.tagIds || (a.tags ? Object.keys(a.tags) : []);
      if (!tIds.some((id) => tagIds.has(id))) return false;
    }

    return true;
  });
}

/**
 * Computes the itemized summary counts required for the collapsed filter bar:
 * total count, number of memories, media, contacts, groups, places, and anchors (plus shares, activities, messages if available).
 */
export function computeFilteredCounts(
  pools: {
    memories?: MemoryModel[];
    media?: MediaModel[];
    contacts?: (Contact | ContactModel)[];
    groups?: GroupModel[];
    places?: PlaceModel[];
    anchors?: AnchorModel[];
    shares?: ShareModel[];
    activities?: ActivityLogModel[];
    messages?: MessageModel[];
  },
  filter: IUnifiedFilter = {},
  screen?: "memories" | "media" | "contacts" | "groups" | "places" | "anchors" | "shares" | "activity" | "messages"
): IFilteredCountsSummary {
  const filteredMemories = filterMemories(pools.memories, filter);
  const filteredMedia = filterMedia(pools.media, filter);
  const filteredContacts = filterContacts(pools.contacts, filter);
  const filteredGroups = filterGroups(pools.groups, filter);
  const filteredPlaces = filterPlaces(pools.places, filter);
  const filteredAnchors = filterAnchors(pools.anchors, filter);
  const filteredShares = filterShares(pools.shares, filter);
  const filteredActivities = filterActivities(pools.activities, filter);
  const filteredMessages = filterMessages(pools.messages, filter);

  const memCount = filteredMemories.length;
  const medCount = filteredMedia.length;
  const conCount = filteredContacts.length;
  const grpCount = filteredGroups.length;
  const placeCount = pools.places ? filteredPlaces.length : undefined;
  const anchorCount = pools.anchors ? filteredAnchors.length : undefined;
  const shareCount = pools.shares ? filteredShares.length : undefined;
  const actCount = pools.activities ? filteredActivities.length : undefined;
  const msgCount = pools.messages ? filteredMessages.length : undefined;

  let total: number;
  if (screen === "memories") {
    total = memCount;
  } else if (screen === "media") {
    total = medCount;
  } else if (screen === "contacts") {
    total = conCount;
  } else if (screen === "groups") {
    total = grpCount;
  } else if (screen === "places") {
    total = placeCount ?? 0;
  } else if (screen === "anchors") {
    total = anchorCount ?? 0;
  } else if (screen === "shares") {
    total = shareCount ?? 0;
  } else if (screen === "activity") {
    total = actCount ?? 0;
  } else if (screen === "messages") {
    total = msgCount ?? 0;
  } else {
    total = memCount + medCount + conCount + grpCount;
    if (placeCount !== undefined) total += placeCount;
    if (anchorCount !== undefined) total += anchorCount;
    if (shareCount !== undefined) total += shareCount;
    if (actCount !== undefined) total += actCount;
    if (msgCount !== undefined) total += msgCount;
  }

  return {
    total,
    memories: memCount,
    media: medCount,
    contacts: conCount,
    groups: grpCount,
    places: placeCount,
    anchors: anchorCount,
    shares: shareCount,
    activities: actCount,
    messages: msgCount,
  };
}
